import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

export default function ResetPasswordPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { loginFromSession } = useAuth();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError(t("rp.no_token") || "Reset-Link ist ungültig oder abgelaufen.");
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError(t("login.pw_short"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/auth/password-reset/confirm`, { token, new_password: password }, { withCredentials: true });
      loginFromSession(r.data);
      setDone(true);
      toast.success(t("rp.success") || "Passwort geändert. Du bist eingeloggt.");
      setTimeout(() => navigate(r.data?.completed_onboarding ? "/app" : "/onboarding"), 900);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      const friendly =
        msg === "invalid_or_used_token" || msg === "invalid_token"
          ? (t("rp.bad_token") || "Link ist ungültig oder wurde bereits verwendet.")
          : msg === "token_expired"
          ? (t("rp.expired") || "Link ist abgelaufen. Bitte neuen anfordern.")
          : msg === "account_deactivated"
          ? "Dieses Konto wurde deaktiviert. Eine Wiederherstellung ist nicht möglich."
          : msg;
      setError(friendly);
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-radial-neon flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="rp-back">
            <ArrowLeft size={14} /> {t("rp.back") || "Zurück"}
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="mb-8 flex justify-center">
          <Logo to="/" />
        </div>
        {done ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center" data-testid="rp-success">
            <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={40} />
            <h2 className="font-display text-2xl font-black">{t("rp.done_h") || "Passwort geändert"}</h2>
            <p className="mt-2 text-sm text-zinc-400">{t("rp.done_sub") || "Alles gut. Du wirst gleich weitergeleitet."}</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black tracking-tight">{t("rp.h") || "Neues Passwort setzen"}</h1>
            <p className="mt-2 text-sm text-zinc-400">{t("rp.sub") || "Mind. 8 Zeichen, 1 Buchstabe + 1 Zahl."}</p>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="rp-form">
              <div>
                <Label htmlFor="rp-pw" className="text-zinc-300">{t("login.password")}</Label>
                <div className="mt-1.5 relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    id="rp-pw"
                    type={show ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pl-10 pr-10 rounded-xl bg-zinc-900 border-white/10 focus-visible:border-neon focus-visible:ring-neon"
                    data-testid="rp-pw-input"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    data-testid="rp-pw-toggle"
                    aria-label={show ? "hide" : "show"}
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {error && (
                <div className="text-xs rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2" data-testid="rp-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || !token}
                className="h-12 rounded-xl bg-neon text-black font-bold hover:bg-neon-hover shadow-neon"
                data-testid="rp-submit-btn"
              >
                {loading ? (t("rp.saving") || "Wird gespeichert…") : (t("rp.save") || "Passwort speichern")}
              </Button>
            </form>
          </>
        )}
        <Footer variant="compact" />
      </div>
    </div>
  );
}
